import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { OverlayRef } from '@angular/cdk/overlay';
import { Subscription } from 'rxjs';
import { DropdownPosition, NgSelectComponent } from '@ng-select/ng-select';
import { DropdownAction } from './dropdown.interface';
import { activeTranslations } from '../../enum/constants';

class DropdownItem {
  [key: string]: any; // or a more specific type if possible
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DropdownComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DropdownComponent implements OnDestroy {
  @Input() label: string = '';
  @Input() items!: DropdownItem[];
  @Input() input: any;
  @Input() bindLabel: string = 'name';
  @Input() bindLabelOption: string = 'name';
  @Input() bindValue!: string;
  @Input() optionColorField?: string;
  @Input() placeholder: any;
  @Input() loading: boolean = false;
  @Input() searchable: boolean = false;
  @Input() selectAll: boolean = false;
  @Input() disabled: boolean = false;
  @Input() addTagText: string = 'Add Item';
  @Input() multiple: boolean = false;
  @Input() closeOnSelect: boolean = !this.multiple;
  @Input() addTag: boolean = false;
  @Input() clearable: boolean = false;
  @Input() readonly: boolean = false;
  @Input() customClass: string = '';
  @Input() virtualScroll: boolean = false;
  @Input() isManagers: boolean = false;
  @Input() dropdownPosition: DropdownPosition = 'auto';
  @Input() isHasActions: boolean = false;
  @Input() actions!: DropdownAction[];
  // Дефолти беруться з перекладів у момент читання, а не один раз при створенні
  // компонента — інакше вони «застигають» мовою, яка була на старті.
  private customNotFoundText?: string;
  private customClearAllText?: string;
  private customTypeToSearch?: string;

  @Input() set notFoundText(value: string) {
    this.customNotFoundText = value;
  }
  get notFoundText(): string {
    return this.customNotFoundText ?? activeTranslations.nothing_found;
  }

  @Input() set clearAllText(value: string) {
    this.customClearAllText = value;
  }
  get clearAllText(): string {
    return this.customClearAllText ?? activeTranslations.clear_selected;
  }

  @Input() set typeToSearch(value: string) {
    this.customTypeToSearch = value;
  }
  get typeToSearch(): string {
    return this.customTypeToSearch ?? activeTranslations.searching;
  }
  @Input() error: boolean = false;
  @Input() isTransparent: boolean = false;
  @Input() isTranslate = false;
  @Input() maxViewElements = 10;
  @Input() hasImg: boolean = false;
  /** Renders app-avatar from `img_url` + label initials instead of a plain image (for people lists) */
  @Input() isAvatar: boolean = false;
  @Input() blur: boolean = true;
  @Input() isAppendToBody: boolean = true;
  @Input() isVertical: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() trackByFn?: (item: DropdownItem) => any;

  @Output() onActionClick = new EventEmitter<any>();
  @Output() onValueChange = new EventEmitter<Event>();
  @Output() onSearchDropdown = new EventEmitter<Event>();
  @Output() clearAll = new EventEmitter<Event>();
  @Output() clear = new EventEmitter<Event>();
  @Output() close = new EventEmitter<Event>();
  @Output() onScrollDropdown = new EventEmitter();
  @Output() onGetRemovableValueChange = new EventEmitter<Event>();

  @ViewChild('select') select!: NgSelectComponent;

  isSelectAll: boolean = false;

  private guardedOverlayRef: OverlayRef | null = null;
  private overlayGuard?: Subscription;

  ngOnDestroy(): void {
    this.overlayGuard?.unsubscribe();
  }

  /**
   * ng-select renders the panel in its own CDK overlay, so a click on an option looks like an
   * outside click to every other open overlay — a surrounding `cdkMenu` would close on the first
   * pick. CDK's outside-click dispatcher skips overlays that have no `outsidePointerEvents`
   * subscribers instead of stopping at them, so an empty subscription is enough to make the panel
   * absorb clicks that land inside it.
   */
  onOpen(): void {
    // `open` fires before the overlay is created, so read it back on the next microtask
    queueMicrotask(() => {
      const overlayRef = (this.select as unknown as { dropdownOverlayRef?: OverlayRef | null })?.dropdownOverlayRef;
      if (!overlayRef || overlayRef === this.guardedOverlayRef) return;
      this.overlayGuard?.unsubscribe();
      this.guardedOverlayRef = overlayRef;
      this.overlayGuard = overlayRef.outsidePointerEvents().subscribe();
    });
  }

  onChange = (data: any) => {};
  onTouch = (_: any) => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(value: string) {
    this.input = value;
    this.onChange(value);
  }

  onchange(event: any) {
    this.onChange(this.input);
    this.onValueChange.emit(event);
    this.select.filter('');
    this.select.itemsList.resetFilteredItems();
    if (this.blur) this.select.blur();
  }

  toggleSelectAll(isSelected?: boolean) {
    this.items.filter((obj) => (obj['selected'] = isSelected));
    if (isSelected == true) {
      this.input = this.items.map((obj) => obj['id']);
    } else {
      this.input = [];
    }
  }

  onRemove(event: any): void {
    this.onGetRemovableValueChange.emit(event.value);
  }

  /**
   *Func triggered on scroll bar go to bottom
   */
  infiniteScroll() {
    this.onScrollDropdown.emit();
  }

  /**
   *Func triggered on input change
   */
  onSearch(event: any) {
    this.onSearchDropdown.emit(event);
  }

  /**
   *Func triggered on input change
   */
  onClear(event?: any) {
    this.clear.emit(event);
    this.clearSearch();
    if (this.blur) this.select.blur();
  }

  clearSearch() {
    this.onSearchDropdown.emit();
  }

  onClose(event?: any) {
    this.close.emit(event);
    if (this.blur) this.select.blur();
  }

  actionClick(e: any): void {
    this.clearSearch();
    const event = {
      action: e,
      term: this.select?.searchTerm,
    };

    this.onActionClick.emit(event);
  }

  removeItem(i: number) {}

  protected readonly Object = Object;

  getSlices(items: any, maxViewElements: number): any[] {
    return items.slice(0, maxViewElements);
  }
}
